import request from "../../utils/request";
import { GoalDashboardStatsView, GoalProgressIndicator } from "@/models/preformanceManagement";
// Helper function to extract payload from Result wrapper
function extractPayload<T>(data: any): T {
  // If data is already the payload (not wrapped in Result), return it
  if (Array.isArray(data)) {
    return data as T;
  }

  // If data has a payload property, extract it
  if (data && typeof data === 'object' && 'payload' in data) {
    return data.payload as T;
  }

  // Otherwise return as-is
  return data as T;
}

// API 1: Get all goals with optional filtering
export async function getAllGoals(filter?: GoalFilterOptions): Promise<GoalView[]> {
  try {
    const response = await request.get(`Goals`, {
      params: filter
    });

    return extractPayload<GoalView[]>(response.data);
  } catch (error: any) {
    let errorMessage = 'Failed to fetch goals';

    if (error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
      errorMessage = error.errors[0].message || error.errors[0].description || errorMessage;
    } else if (error.data?.errors && Array.isArray(error.data.errors) && error.data.errors.length > 0) {
      errorMessage = error.data.errors[0].message || error.data.errors[0].description || errorMessage;
    } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors) && error.response.data.errors.length > 0) {
      errorMessage = error.response.data.errors[0].message || error.response.data.errors[0].description || errorMessage;
    } else if (error.message) {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
}

// API 2: Get goal by ID
export async function getGoalById(goalId: string): Promise<GoalView> {
  try {
    const response = await request.get(`Goals/${goalId}`);

    return extractPayload<GoalView>(response.data);
  } catch (error: any) {
    if (error.response?.data?.errors && error.response.data.errors.length > 0) {
      const errorMessage = error.response.data.errors[0].message || error.response.data.errors[0].description;
      throw new Error(errorMessage);
    }

    throw error;
  }
}

// API 3: Create new goal
export async function createGoal(input: GoalInput): Promise<GoalView> {
  try {
    const response = await request.post(`Goals`, input);

    return extractPayload<GoalView>(response.data);
  } catch (error: any) {
    if (error.response?.data?.errors && error.response.data.errors.length > 0) {
      const errorMessage = error.response.data.errors[0].message || error.response.data.errors[0].description;
      throw new Error(errorMessage);
    }

    throw error;
  }
}

// API 4: Update existing goal
export async function updateGoal(goalId: string, input: GoalInput): Promise<GoalView> {
  try {
    const response = await request.put(`Goals/${goalId}`, input);

    return extractPayload<GoalView>(response.data);
  } catch (error: any) {
    if (error.response?.data?.errors && error.response.data.errors.length > 0) {
      const errorMessage = error.response.data.errors[0].message || error.response.data.errors[0].description;
      throw new Error(errorMessage);
    }

    throw error;
  }
}

// API 5: Delete goal
export async function deleteGoal(goalId: string): Promise<boolean> {
  try {
    const response = await request.delete(`Goals/${goalId}`);

    return response.data.status === 200 || response.status === 200;
  } catch (error: any) {
    if (error.response?.data?.errors && error.response.data.errors.length > 0) {
      const errorMessage = error.response.data.errors[0].message || error.response.data.errors[0].description;
      throw new Error(errorMessage);
    }

    throw error;
  }
}

// API 6: Update goal progress
export async function updateGoalProgress(input: GoalProgressUpdateInput): Promise<GoalProgressUpdateView> {
  try {
    const response = await request.put(`Goals/${input.goalId}/progress`, input);

    return extractPayload<GoalProgressUpdateView>(response.data);
  } catch (error: any) {
    if (error.response?.data?.errors && error.response.data.errors.length > 0) {
      const errorMessage = error.response.data.errors[0].message || error.response.data.errors[0].description;
      throw new Error(errorMessage);
    }

    throw error;
  }
}

// API 8: Get goal statistics
export async function getGoalStats(filter?: GoalFilterOptions): Promise<GoalStatsView> {
  try {
    const response = await request.get(`Goals/stats`, {
      params: filter
    });

    return extractPayload<GoalStatsView>(response.data);
  } catch (error: any) {
    if (error.response?.data?.errors && error.response.data.errors.length > 0) {
      const errorMessage = error.response.data.errors[0].message || error.response.data.errors[0].description;
      throw new Error(errorMessage);
    }

    throw error;
  }
}

// Get user's company departments
export async function getUserCompanyDepartments(): Promise<DepartmentBasicView[]> {
  try {
    const response = await request.get(`Goals/departments`);

    return extractPayload<DepartmentBasicView[]>(response.data);
  } catch (error: any) {
    let errorMessage = 'Failed to fetch departments';

    if (error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
      errorMessage = error.errors[0].message || error.errors[0].description || errorMessage;
    } else if (error.data?.errors && Array.isArray(error.data.errors) && error.data.errors.length > 0) {
      errorMessage = error.data.errors[0].message || error.data.errors[0].description || errorMessage;
    } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors) && error.response.data.errors.length > 0) {
      errorMessage = error.response.data.errors[0].message || error.response.data.errors[0].description || errorMessage;
    } else if (error.message) {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
}

// Get available owners for goals (role-based filtering)
export async function getAvailableOwners(): Promise<GoalOwnerView[]> {
  try {
    const response = await request.get(`Goals/available-owners`);

    return extractPayload<GoalOwnerView[]>(response.data);
  } catch (error: any) {
    let errorMessage = 'Failed to fetch available owners';

    if (error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
      errorMessage = error.errors[0].message || error.errors[0].description || errorMessage;
    } else if (error.data?.errors && Array.isArray(error.data.errors) && error.data.errors.length > 0) {
      errorMessage = error.data.errors[0].message || error.data.errors[0].description || errorMessage;
    } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors) && error.response.data.errors.length > 0) {
      errorMessage = error.response.data.errors[0].message || error.response.data.errors[0].description || errorMessage;
    } else if (error.message) {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
}

export async function getGoalDashboardStats(): Promise<GoalDashboardStatsView> {
  try {
    const response = await request.get(`Goals/dashboard/stats`);

    return extractPayload<GoalDashboardStatsView>(response.data);
  } catch (error: any) {
    let errorMessage = 'Failed to fetch goal dashboard statistics';

    if (error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
      errorMessage = error.errors[0].message || error.errors[0].description || errorMessage;
    } else if (error.data?.errors && Array.isArray(error.data.errors) && error.data.errors.length > 0) {
      errorMessage = error.data.errors[0].message || error.data.errors[0].description || errorMessage;
    } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors) && error.response.data.errors.length > 0) {
      errorMessage = error.response.data.errors[0].message || error.response.data.errors[0].description || errorMessage;
    } else if (error.message) {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
}

// API: Get goal progress indicators
export async function getGoalProgressIndicators(): Promise<GoalProgressIndicator[]> {
  try {
    const response = await request.get(`Goals/dashboard/progress-indicators`);

    return extractPayload<GoalProgressIndicator[]>(response.data);
  } catch (error: any) {
    let errorMessage = 'Failed to fetch goal progress indicators';

    if (error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
      errorMessage = error.errors[0].message || error.errors[0].description || errorMessage;
    } else if (error.data?.errors && Array.isArray(error.data.errors) && error.data.errors.length > 0) {
      errorMessage = error.data.errors[0].message || error.data.errors[0].description || errorMessage;
    } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors) && error.response.data.errors.length > 0) {
      errorMessage = error.response.data.errors[0].message || error.response.data.errors[0].description || errorMessage;
    } else if (error.message) {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
}

// Achievement APIs
export async function hasGoalGetterAchievement(): Promise<boolean> {
  try {
    const response = await request.get(`Goals/achievements/goal-getter`);

    return extractPayload<boolean>(response.data);
  } catch (error: any) {
    throw new Error(error.message || 'Failed to check Goal Getter achievement');
  }
}

export async function hasConsistentAchieverAchievement(): Promise<boolean> {
  try {
    const response = await request.get(`Goals/achievements/consistent-achiever`);

    return extractPayload<boolean>(response.data);
  } catch (error: any) {
    throw new Error(error.message || 'Failed to check Consistent Achiever achievement');
  }
}

export async function getGoalAchievementProgress(): Promise<GoalAchievementProgressView> {
  try {
    const response = await request.get(`Goals/achievements/progress`);

    return extractPayload<GoalAchievementProgressView>(response.data);
  } catch (error: any) {
    throw new Error(error.message || 'Failed to get goal achievement progress');
  }
}
// API: Get goal alignment tree (hierarchical)
export async function getGoalTree(rootGoalId?: string): Promise<GoalTreeView> {
  try {
    const params: Record<string, string> = {};
    if (rootGoalId) params.rootGoalId = rootGoalId;
    const response = await request.get(`Goals/tree`, { params });
    return extractPayload<GoalTreeView>(response.data);
  } catch (error: any) {
    let errorMessage = 'Failed to fetch goal tree';
    if (error.response?.data?.errors?.length > 0) {
      errorMessage = error.response.data.errors[0].message || error.response.data.errors[0].description || errorMessage;
    } else if (error.message) {
      errorMessage = error.message;
    }
    throw new Error(errorMessage);
  }
}

// Types
export interface GoalTreeView {
  goal?: GoalView;
  children?: GoalTreeView[];
}

export interface GoalFilterOptions {
  department?: string;
  status?: string;
  level?: string;
  priority?: string;
  tags?: string;
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
}

export interface GoalInput {
  title: string;
  description: string;
  level: number;
  status: number;
  progress: number;
  ownerId: string;
  departmentId?: number | null;
  categoryId?: string | null;
  parentId?: string | null;
  startDate: string;
  dueDate: string;
  priority: number;
  metricType?: number | null;
  skillIds?: string | null;
  milestones?: GoalMilestoneInput[];
  assignments?: GoalAssignmentInput[];
  tags?: string[];
}

export interface GoalMilestoneInput {
  title: string;
  description: string;
  isCompleted: boolean;
  dueDate: string;
  order: number;
}

export interface GoalAssignmentInput {
  userId: string;
  assignmentType: number;
}

export interface GoalView {
  id: string;
  title: string;
  description: string;
  level: string | number;
  status: number;
  progress: number;
  userId: string;
  departmentId?: number;
  categoryId?: string;
  parentId?: string;
  startDate: string;
  dueDate: string;
  completedDate?: string;
  priority: number;
  metricType?: number | null;
  metricTypeName?: string | null;
  skillIds?: string | null;
  isDeleted: boolean;
  createdAt: string;
  updatedAt?: string;
  owner?: {
    id: string;
    name?: string;
    email?: string;
  };
  department?: {
    id: number;
    name: string;
  };
  category?: {
    id: string;
    name: string;
  };
  milestones?: any[];
  comments?: any[];
  assignments?: any[];
  tags?: string[];
  hasActiveBlockers?: boolean;
  activeBlockerCount?: number;
}

export interface GoalProgressUpdateInput {
  goalId: string;
  progress: number;
  status?: number;
  notes?: string;
  updatedAt?: string;
}

export interface GoalProgressUpdateView {
  id: number;
  goalId: string;
  userId: string;
  previousProgress: number;
  newProgress: number;
  previousStatus: number;
  newStatus: number;
  note?: string;
  createdAt: string;
}

export interface GoalStatsView {
  totalGoals: number;
  completedGoals: number;
  onTrackGoals: number;
  atRiskGoals: number;
  overdueGoals: number;
  totalMilestones: number;
  completedMilestones: number;
  goalsByLevel: { [key: string]: number };
  goalsByStatus: { [key: string]: number };
  averageProgress?: number;
}

export interface DepartmentBasicView {
  id: number;
  name: string;
}

export interface GoalOwnerView {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  departmentId?: number;
  departmentName?: string;
  profilePictureUrl?: string;
}

export interface GoalAchievementProgressView {
  goalGetterProgress: number;
  goalGetterTarget: number;
  goalGetterUnlocked: boolean;
  consistentAchieverProgress: number;
  consistentAchieverTarget: number;
  consistentAchieverUnlocked: boolean;
}

// Audit Log Types
export interface FieldChangeView {
  field: string;
  oldValue?: string;
  newValue?: string;
}

export interface GoalAuditLogView {
  id: string;
  goalId: string;
  changedById: string;
  changedByName?: string;
  changedAt: string;
  changes: FieldChangeView[];
  comment?: string;
}

// Blocker Types
export interface GoalBlockerView {
  id: string;
  goalId: string;
  description: string;
  severity: string;
  requiredResources?: string;
  status: string;
  resolutionNote?: string;
  resolvedByName?: string;
  resolvedAt?: string;
  createdByName?: string;
  createdAt: string;
}

export interface GoalBlockerInput {
  description: string;
  severity: number;
  requiredResources?: string;
}

export interface GoalBlockerResolveInput {
  resolutionNote: string;
}

// Audit Log API
export async function getGoalAuditLog(goalId: string): Promise<GoalAuditLogView[]> {
  try {
    const response = await request.get(`Goals/${goalId}/audit-log`);
    return extractPayload<GoalAuditLogView[]>(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.errors?.[0]?.message || error.message || 'Failed to fetch audit log');
  }
}

// Blocker APIs
export async function getGoalBlockers(goalId: string): Promise<GoalBlockerView[]> {
  try {
    const response = await request.get(`Goals/${goalId}/blockers`);
    return extractPayload<GoalBlockerView[]>(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.errors?.[0]?.message || error.message || 'Failed to fetch blockers');
  }
}

export async function createGoalBlocker(goalId: string, input: GoalBlockerInput): Promise<GoalBlockerView> {
  try {
    const response = await request.post(`Goals/${goalId}/blockers`, input);
    return extractPayload<GoalBlockerView>(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.errors?.[0]?.message || error.message || 'Failed to create blocker');
  }
}

export async function resolveGoalBlocker(blockerId: string, input: GoalBlockerResolveInput): Promise<GoalBlockerView> {
  try {
    const response = await request.put(`Goals/blockers/${blockerId}/resolve`, input);
    return extractPayload<GoalBlockerView>(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.errors?.[0]?.message || error.message || 'Failed to resolve blocker');
  }
}

export async function deleteGoalBlocker(blockerId: string): Promise<boolean> {
  try {
    const response = await request.delete(`Goals/blockers/${blockerId}`);
    return response.data.status === 200 || response.status === 200;
  } catch (error: any) {
    throw new Error(error.response?.data?.errors?.[0]?.message || error.message || 'Failed to delete blocker');
  }
}

// Milestone Types
export interface GoalMilestoneView {
  id: string;
  title: string;
  description?: string;
  isCompleted: boolean;
  status: 'Pending' | 'InProgress' | 'Completed' | 'Overdue';
  dueDate?: string;
  completedDate?: string;
  order: number;
}

export interface GoalMilestoneInput {
  title: string;
  description?: string;
  isCompleted: boolean;
  status?: number;
  dueDate?: string;
  order: number;
}

// Outcome Metrics Types
export interface GoalOutcomeMetricsView {
  goalId: string;
  totalMilestones: number;
  completedMilestones: number;
  inProgressMilestones: number;
  overdueMilestones: number;
  milestoneProgressPercent: number;
  expectedReviews: number;
  actualReviews: number;
  cadenceAdherencePercent: number;
  projectedCompletionDate?: string;
  daysRemaining: number;
  daysElapsed: number;
  totalDays: number;
  velocityPercent: number;
  projectionStatus: 'OnTrack' | 'AtRisk' | 'Behind' | 'Completed';
}

// Milestone APIs
export async function getGoalMilestones(goalId: string): Promise<GoalMilestoneView[]> {
  try {
    const response = await request.get(`Goals/${goalId}/milestones`);
    return extractPayload<GoalMilestoneView[]>(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.errors?.[0]?.message || error.message || 'Failed to fetch milestones');
  }
}

export async function addGoalMilestone(goalId: string, input: GoalMilestoneInput): Promise<GoalMilestoneView> {
  try {
    const response = await request.post(`Goals/${goalId}/milestones`, input);
    return extractPayload<GoalMilestoneView>(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.errors?.[0]?.message || error.message || 'Failed to add milestone');
  }
}

export async function updateGoalMilestone(milestoneId: string, input: GoalMilestoneInput): Promise<GoalMilestoneView> {
  try {
    const response = await request.put(`Goals/milestones/${milestoneId}`, input);
    return extractPayload<GoalMilestoneView>(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.errors?.[0]?.message || error.message || 'Failed to update milestone');
  }
}

export async function deleteGoalMilestone(milestoneId: string): Promise<boolean> {
  try {
    const response = await request.delete(`Goals/milestones/${milestoneId}`);
    return response.data.status === 200 || response.status === 200;
  } catch (error: any) {
    throw new Error(error.response?.data?.errors?.[0]?.message || error.message || 'Failed to delete milestone');
  }
}

export async function toggleGoalMilestone(milestoneId: string): Promise<boolean> {
  try {
    const response = await request.put(`Goals/milestones/${milestoneId}/toggle`);
    return extractPayload<boolean>(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.errors?.[0]?.message || error.message || 'Failed to toggle milestone');
  }
}

// Outcome Metrics API
export async function getGoalOutcomeMetrics(goalId: string): Promise<GoalOutcomeMetricsView> {
  try {
    const response = await request.get(`Goals/${goalId}/outcome-metrics`);
    return extractPayload<GoalOutcomeMetricsView>(response.data);
  } catch (error: any) {
    throw new Error(error.response?.data?.errors?.[0]?.message || error.message || 'Failed to fetch outcome metrics');
  }
}
